// The commands every view of a pull request answers (the page around the
// views), and the app's own under every page.

import { stepText } from '../logic/prStack';
import type { PrTab } from '../router';
import { times, type Command, type Place } from './command';
import { leave, nothingFor, notApplicable, pass, type Update } from './effects';

export interface PageModel {
  tab: PrTab;
  /** Why nothing can be written here (the App does not cover the repository); null while it can. */
  readOnly: string | null;
  stack: PageStack;
}

/**
 * The pull request's stack, as the page knows it: still loading, failed to
 * load, none (it is in no stack), or the pull requests the stack motions
 * step through below it (towards the base) and above it, nearest first.
 */
export type PageStack = { is: 'loading' } | { is: 'failed' } | { is: 'none' } | { is: 'known'; below: readonly string[]; above: readonly string[] };

export type PageEffect =
  | { kind: 'navigate'; tab: PrTab }
  /** On the Review view already: put the focus on its publish button. */
  | { kind: 'focus-publish' }
  | { kind: 'scroll-window'; to: 'top' | 'end' }
  | { kind: 'find-file' }
  | { kind: 'show-guide' }
  /** Open the header's stack menu (which says why, when the stack could not be read). */
  | { kind: 'open-stack' }
  /** Go to this pull request of the stack, on the view shown. */
  | { kind: 'open-pr'; id: string }
  /** Say why nothing can be written, with the way to install the App. */
  | { kind: 'read-only' };

const TABS: Partial<Record<Place, PrTab>> = { overview: 'overview', files: 'files', commits: 'commits', review: 'review' };

/** What a stack command says where there is no stack to act on. */
const OUTSIDE_STACK = { none: 'This pull request is not part of a stack.', loading: 'This pull request’s stack is still loading.' } as const;

export const TAB_TITLE: Readonly<Record<PrTab, string>> = { overview: 'Overview', files: 'Files', commits: 'Snapshots', review: 'Review' };

/** What `command` does on a pull request's page, whichever view shows: the view above has already passed it on. */
export function updatePage(m: PageModel, command: Command): Update<PageModel, PageEffect> {
  switch (command.kind) {
    case 'goto': {
      if (command.place === 'top' || command.place === 'end') return [m, [{ kind: 'scroll-window', to: command.place }]];
      if (command.place === 'named-file') return [m, [{ kind: 'find-file' }]];
      const tab = TABS[command.place]!;
      if (tab !== 'review') return [m, tab === m.tab ? [] : [{ kind: 'navigate', tab }]];
      if (m.tab === 'review') return [m, [{ kind: 'focus-publish' }]];
      // As the header's disabled publish button: say why instead of going to a page that can only fail.
      return [m, [m.readOnly !== null ? { kind: 'read-only' } : { kind: 'navigate', tab }]];
    }
    case 'open':
      if (command.menu === 'guide') return [m, [{ kind: 'show-guide' }]];
      if (command.menu === 'finder') return [m, [notApplicable('The file picker is in Files', { kind: 'goto', place: 'files' })]];
      if (command.menu === 'stack') return [m, [m.stack.is === 'failed' || m.stack.is === 'known' ? { kind: 'open-stack' } : notApplicable(OUTSIDE_STACK[m.stack.is])]];
      return [m, [pass]];
    case 'move': {
      if (command.object !== 'pr' || command.only) return [m, [nothingFor(command, `in ${TAB_TITLE[m.tab]}`)]];
      // A stack that could not be read: the menu says why.
      if (m.stack.is === 'failed') return [m, [{ kind: 'open-stack' }]];
      if (m.stack.is !== 'known') return [m, [notApplicable(OUTSIDE_STACK[m.stack.is])]];
      const way = command.dir > 0 ? m.stack.above : m.stack.below;
      if (!way.length) return [m, [notApplicable(stepText(command.dir > 0 ? 'above' : 'below'))]];
      return [m, [{ kind: 'open-pr', id: way[Math.min(times(command), way.length) - 1] }]];
    }
    case 'select':
    case 'range':
    case 'grow':
    case 'act':
    case 'fold':
    case 'present':
    case 'scroll':
      return [m, [nothingFor(command, `in ${TAB_TITLE[m.tab]}`)]];
    case 'refresh':
    case 'submit':
    case 'cancel':
      return [m, [pass]];
  }
}

export interface AppModel {
  signedIn: boolean;
  /** The Settings page is the one shown. */
  inSettings: boolean;
}

export type AppEffect = { kind: 'show-feedback' } | { kind: 'open-settings' };

/** The app's own commands, under every page's: feedback and Settings, for someone signed in (the key is left alone otherwise). */
export function updateApp(m: AppModel, command: Command): Update<AppModel, AppEffect> {
  if (command.kind !== 'open' || (command.menu !== 'feedback' && command.menu !== 'settings')) return [m, [pass]];
  if (!m.signedIn) return [m, [leave]];
  if (command.menu === 'feedback') return [m, [{ kind: 'show-feedback' }]];
  return [m, [m.inSettings ? notApplicable('Settings is the page shown') : { kind: 'open-settings' }]];
}

export type SettingsEffect = { kind: 'leave-settings' };

/** The Settings page's one command of its own: leaving it. */
export function updateSettingsPage<M>(m: M, command: Command): Update<M, SettingsEffect> {
  return [m, [command.kind === 'cancel' ? { kind: 'leave-settings' } : pass]];
}

export type SendEffect = { kind: 'publish' };

/** The Review view's one command of its own: sending the review. */
export function updateSend<M>(m: M, command: Command): Update<M, SendEffect> {
  return [m, [command.kind === 'submit' ? { kind: 'publish' } : pass]];
}
